import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useServerConnection } from '../../contexts/ServerConnectionContext';
import { parseMinecraftFormatting } from '../../utils/minecraftFormatting';
import { useAuth } from '../../contexts/AuthContext';

const ServerInfoCard = () => {
  const [inputIpAddress, setInputIpAddress] = useState('');
  const {
    serverInfo,
    isConnectedToServer,
    isConnecting,
    connectionError,
    targetIp,
    connectToServer,
    disconnectFromServer
  } = useServerConnection();
  const { isAuthenticated, triggerGoogleLogin } = useAuth(); // Get auth state and login trigger

  // Keep input in sync if external data changes (e.g., navigating directly with state)
  useEffect(() => {
    if (!isConnectedToServer && targetIp) {
      setInputIpAddress(targetIp);
    } else if (!isConnectedToServer && !targetIp) {
      setInputIpAddress('');
    }
    // No need to clear here, disconnect handles it
  }, [targetIp, isConnectedToServer]);

  const handleConnectClick = () => {
    if (!isAuthenticated) {
      console.log("ServerInfoCard: User not authenticated, triggering Google Login...");
      triggerGoogleLogin(); // Call the login trigger from context
      return; // Stop further action until login completes
    }
    // Call the prop function passed from the parent (HomePage)
    connectToServer(inputIpAddress);
  };

  const handleDisconnectClick = () => {
    disconnectFromServer(true);
    setInputIpAddress('');
  };

  const cardVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2, ease: "easeIn" } },
  };

  // Helper to safely access nested player data
  const getPlayerCountString = (players) => {
    if (!players) return '? / ?';
    const online = players.online?.toLocaleString() ?? '?';
    const max = players.max?.toLocaleString() ?? '?';
    return `${online} / ${max}`;
  };

  // Helper to display MOTD (handling different formats)
  const renderMotd = (motdData) => {
    if (!motdData) return null;

    let sourceString = '';
    // Prioritize 'raw' for formatting codes, then 'html' (less likely to have §), then 'clean'
    if (typeof motdData === 'object' && motdData.raw) {
      sourceString = motdData.raw;
    } else if (typeof motdData === 'string') {
      sourceString = motdData; // Assume string might have codes
    } else if (typeof motdData === 'object' && motdData.html) {
      // If only HTML is available, we *can't* parse § codes. Render it as is (or strip tags).
      // For now, let's just show the clean version as fallback.
      sourceString = motdData.clean || '';
    } else if (typeof motdData === 'object' && motdData.clean) {
      sourceString = motdData.clean;
    } else {
      return null; // Cannot determine source string
    }

    // Split into lines first
    const lines = sourceString.split('\n');
    const renderedLines = lines.map((line, index) => (
      // Parse each line individually
      <div key={index} className="whitespace-pre-wrap break-words">
        {parseMinecraftFormatting(line)}
      </div>
    ));

    return (
      <div className="mt-4 p-3 bg-dark-700 rounded-xl text-sm hover:bg-dark-600 transition-medium relative overflow-hidden border border-dark-500">
        <div className="flex items-center mb-2 group/motd">
          <i className="fas fa-quote-left text-accent-blue mr-2 group-hover/motd:rotate-12 transition-medium"></i>
          <span className="text-gray-400 group-hover/motd:text-white transition-medium">Server MOTD</span>
        </div>
        <div className="font-minecraft text-sm leading-normal pl-2 border-l-2 border-accent-blue group-hover:border-accent-purple transition-colors">
          {/* Render the array of React elements */}
          {renderedLines}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8 bg-dark-600 rounded-2xl p-6 relative overflow-hidden transform-gpu hover:scale-[1.005] transition-medium border border-dark-400 hover:border-accent-purple/50 hover:shadow-glow-purple group">
      {/* ... background effects ... */}
      <div className="absolute -right-20 -top-20 w-64 h-64 bg-accent-purple opacity-[0.04] rounded-full blur-3xl group-hover:opacity-[0.08] transition-opacity duration-300"></div>
      <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-accent-blue opacity-[0.04] rounded-full blur-3xl group-hover:opacity-[0.08] transition-opacity duration-300"></div>

      <AnimatePresence mode="wait">
        {isConnecting ? (
          // --- Loading State --- (Keep as is)
          <motion.div
            key="loading"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="animate-pulse min-h-[150px] flex flex-col justify-center"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-lg bg-dark-500 mr-3"></div>
              <div className="flex-1">
                <div className="h-5 bg-dark-500 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-dark-500 rounded w-1/2"></div>
              </div>
            </div>
            <div className="h-4 bg-dark-500 rounded w-full mt-2"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <i className="fas fa-spinner fa-spin text-accent-purple text-2xl"></i>
            </div>
          </motion.div>
        ) : serverInfo ? (
          // --- Connected State ---
          <motion.div
            key="connected"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative z-10"
          >
            <div className="flex flex-col md:flex-row justify-between">
              {/* Server Details */}
              <div className="mb-4 md:mb-0">
                <div className="flex items-center mb-2 group/info">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-blue to-accent-green flex items-center justify-center mr-3 transition-medium transform-gpu group-hover/info:rotate-12">
                    <i className="fas fa-server text-sm"></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold group-hover/info:text-accent-blue transition-medium">
                      {serverInfo.hostname || serverInfo.ip || 'Connected Server'}
                    </h2>
                    {/* Updated details display */}
                    <div className="flex flex-wrap items-center text-sm text-gray-400 gap-x-3 gap-y-1">
                      <span className="flex items-center hover:text-white transition-medium">
                        <i className={`fas fa-signal mr-1 ${serverInfo.online ? 'text-accent-green animate-pulse-slow' : 'text-accent-red'}`}></i>
                        {serverInfo.online ? 'Online' : 'Offline'}
                      </span>
                      <span className="flex items-center hover:text-white transition-medium">
                        <i className="fas fa-users mr-1"></i>
                        {getPlayerCountString(serverInfo.players)}
                      </span>
                      {serverInfo.ping !== undefined && ( // Check if ping exists
                        <span className="flex items-center hover:text-white transition-medium">
                          <i className="fas fa-tachometer-alt mr-1"></i> {serverInfo.ping}{typeof serverInfo.ping === 'number' ? 'ms' : ''} {/* Add ms if it's just a number */}
                        </span>
                      )}
                      {serverInfo.version && (
                        <span className="flex items-center hover:text-white transition-medium">
                          <i className="fas fa-code-branch mr-1"></i> {serverInfo.version}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Action Button - Calls parent's disconnect handler */}
              <div className="flex items-start">
                <button
                  onClick={handleDisconnectClick} // Uses the internal handler that calls the prop
                  className="bg-dark-500 hover:bg-dark-400 text-white px-4 py-2 rounded-xl text-sm transition-medium transform-gpu hover:-translate-y-0.5 active:translate-y-0 flex items-center"
                >
                  <i className="fas fa-times mr-2"></i> Disconnect
                </button>
              </div>
            </div>
            {/* Display MOTD */}
            {renderMotd(serverInfo.motd)}
          </motion.div>
        ) : (
          // --- Unconnected State --- (Input form)
          <motion.div
            key="unconnected"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="relative z-10"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex-1 w-full">
                <label htmlFor="server-ip-input" className="block text-sm font-medium text-gray-300 mb-2 group-hover:text-white transition-colors">
                  Enter Server IP to Connect
                </label>
                <div className="relative">
                  <i className="fas fa-server absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 peer-focus:text-accent-purple transition-colors"></i>
                  <input
                    id="server-ip-input"
                    type="text"
                    placeholder="e.g., mc.hypixel.net or 127.0.0.1"
                    className="peer w-full bg-dark-700 rounded-xl py-3 px-4 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-accent-purple/50 border border-dark-400 hover:border-dark-300 transition-medium"
                    value={inputIpAddress}
                    onChange={(e) => setInputIpAddress(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleConnectClick(); }} // Connect on Enter
                  />
                </div>
                {/* Display error from props */}
                {connectionError && !isAuthenticated && !isConnecting && (
                  // Show login prompt if error occurred *before* auth check was done
                  <motion.p initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="text-accent-yellow text-xs mt-2">
                    <i className="fas fa-info-circle mr-1"></i> Please login to connect to servers.
                  </motion.p>
                )}
                {connectionError && isAuthenticated && !isConnecting && (
                  // Show actual connection error if user *is* authenticated
                  <motion.p initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="text-accent-red text-xs mt-2">
                    <i className="fas fa-exclamation-circle mr-1"></i> {connectionError}
                  </motion.p>
                )}
              </div>
              <div className="w-full md:w-auto mt-3 md:mt-0 md:self-end">
                {/* Connect button calls parent's connect handler */}
                <button
                  className="w-full bg-gradient-to-r from-accent-purple to-accent-blue text-white px-6 py-3 rounded-xl hover:shadow-glow active:translate-y-0.5 transform-gpu transition-medium font-medium whitespace-nowrap group/btn disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:shadow-none"
                  onClick={handleConnectClick} // Uses the internal handler that calls the prop
                  disabled={!inputIpAddress.trim() || isConnecting}
                >
                  <i className={`fas ${isAuthenticated ? 'fa-plug' : 'fa-sign-in-alt'} mr-2 group-hover/btn:animate-ping-slow transition-medium`}></i>
                  {isAuthenticated ? 'Connect' : 'Login to Connect'}
                </button>
              </div>
            </div>
            {!isAuthenticated && (
              <p className="text-xs text-gray-500 mt-4 text-center md:text-left group-hover:text-gray-400 transition-colors">
                Login required to access server testing features.
              </p>
            )}
            {isAuthenticated && !isConnectedToServer && !isConnecting && (
              <p className="text-xs text-gray-500 mt-4 text-center md:text-left group-hover:text-gray-400 transition-colors">
                Connect to a server to begin analysis and testing.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


// const ServerInfoCard = ({ serverData, isLoading }) => {
//   const [isRefreshing, setIsRefreshing] = useState(false);
//   const [hoveredSection, setHoveredSection] = useState(null);

//   const handleRefresh = () => {
//     setIsRefreshing(true);
//     // Simulate refresh
//     setTimeout(() => setIsRefreshing(false), 1000);
//   };

//   return (
//     <div className="mb-8 bg-dark-600 rounded-2xl p-6 relative overflow-hidden transform-gpu hover:scale-[1.01] transition-medium border border-dark-400 hover:border-accent-purple/50 hover:shadow-glow group">
//       {/* Background glow effects */}
//       <div className="absolute -right-20 -top-20 w-64 h-64 bg-accent-purple opacity-5 rounded-full blur-3xl group-hover:opacity-10 transition-medium"></div>
//       <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-accent-blue opacity-5 rounded-full blur-3xl group-hover:opacity-10 transition-medium"></div>

//       {/* Floating particles */}
//       <div className="absolute inset-0 pointer-events-none">
//         <div className="particle-1"></div>
//         <div className="particle-2"></div>
//       </div>

//       {isLoading ? (
//         <div className="animate-pulse space-y-4">
//           <div className="h-10 bg-dark-500 rounded transform-gpu transition-medium hover:scale-[1.01]"></div>
//           <div className="h-20 bg-dark-500 rounded transform-gpu transition-medium hover:scale-[1.01]"></div>
//         </div>
//       ) : serverData ? (
//         <>
//           <div className="flex flex-col md:flex-row justify-between relative z-10">
//             <div 
//               className="mb-4 md:mb-0 transform-gpu transition-medium hover:scale-[1.02]"
//               onMouseEnter={() => setHoveredSection('info')}
//               onMouseLeave={() => setHoveredSection(null)}
//             >
//               <div className="flex items-center mb-2 group">
//                 <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-medium transform-gpu ${
//                   hoveredSection === 'info' ? 
//                     'bg-gradient-to-br from-accent-blue to-accent-purple rotate-12 scale-110' : 
//                     'bg-gradient-to-br from-accent-pink to-accent-purple'
//                 }`}>
//                   <i className="fas fa-server text-sm"></i>
//                 </div>
//                 <div>
//                   <h2 className={`text-xl font-bold transition-medium ${
//                     hoveredSection === 'info' ? 'text-accent-blue animate-pulse-text' : 'group-hover:text-accent-red'
//                   }`}>
//                     {serverData.ip || 'Unknown Server'}
//                   </h2>
//                   <div className="flex flex-wrap items-center text-sm text-gray-400 gap-3">
//                     <span className={`flex items-center transition-medium ${
//                       hoveredSection === 'info' ? 'text-white' : 'hover:text-white'
//                     }`}>
//                       <i className={`fas fa-signal mr-1 ${
//                         serverData.status === 'Online' ? 'text-accent-green' : 'text-accent-red'
//                       }`}></i> 
//                       {serverData.status || 'Unknown'}
//                     </span>
//                     <span className={`flex items-center transition-medium ${
//                       hoveredSection === 'info' ? 'text-white' : 'hover:text-white'
//                     }`}>
//                       <i className="fas fa-users mr-1"></i> 
//                       {serverData.players || '0'} players
//                     </span>
//                     <span className={`flex items-center transition-medium ${
//                       hoveredSection === 'info' ? 'text-white' : 'hover:text-white'
//                     }`}>
//                       <i className="fas fa-clock mr-1"></i> 
//                       Ping: {serverData.ping || 'N/A'}
//                     </span>
//                   </div>
//                 </div>
//               </div>
//             </div>
//             <div 
//               className="flex flex-wrap items-center gap-3"
//               onMouseEnter={() => setHoveredSection('actions')}
//               onMouseLeave={() => setHoveredSection(null)}
//             >
//               <div className={`px-3 py-1.5 rounded-lg text-sm transition-medium transform-gpu ${
//                 hoveredSection === 'actions' ? 
//                   'bg-dark-400 scale-105' : 
//                   'bg-dark-500 hover:bg-dark-400 hover:scale-[1.03]'
//               }`}>
//                 <span className={`mr-1 transition-medium ${
//                   hoveredSection === 'actions' ? 'text-white' : 'text-gray-400 hover:text-white'
//                 }`}>Version:</span>
//                 <span className={`transition-medium ${
//                   hoveredSection === 'actions' ? 'text-accent-blue' : 'hover:text-accent-blue'
//                 }`}>{serverData.version || 'Unknown'}</span>
//               </div>
//               <div className={`px-3 py-1.5 rounded-lg text-sm transition-medium transform-gpu ${
//                 hoveredSection === 'actions' ? 
//                   'bg-dark-400 scale-105' : 
//                   'bg-dark-500 hover:bg-dark-400 hover:scale-[1.03]'
//               }`}>
//                 <span className={`mr-1 transition-medium ${
//                   hoveredSection === 'actions' ? 'text-white' : 'text-gray-400 hover:text-white'
//                 }`}>IP:</span>
//                 <span className={`transition-medium ${
//                   hoveredSection === 'actions' ? 'text-accent-blue' : 'hover:text-accent-blue'
//                 }`}>{serverData.realIp || 'N/A'}</span>
//               </div>
//               <button 
//                 onClick={handleRefresh}
//                 disabled={isRefreshing}
//                 className={`px-3 py-1.5 rounded-lg text-sm transition-medium transform-gpu ${
//                   hoveredSection === 'actions' ? 
//                     'bg-dark-400 scale-105' : 
//                     'bg-dark-500 hover:bg-dark-400 hover:scale-[1.03]'
//                 } ${isRefreshing ? 'opacity-70' : ''}`}
//               >
//                 <i className={`fas fa-sync-alt mr-1 ${
//                   isRefreshing ? 'animate-spin' : 'group-hover:rotate-180 transition-medium'
//                 }`}></i> 
//                 Refresh
//               </button>
//             </div>
//           </div>

//           {serverData.motd && (
//             <div 
//               className="mt-4 p-3 bg-dark-700 rounded-xl text-sm transition-medium relative overflow-hidden transform-gpu hover:scale-[1.01] hover:bg-dark-600"
//               onMouseEnter={() => setHoveredSection('motd')}
//               onMouseLeave={() => setHoveredSection(null)}
//             >
//               <div className={`absolute -right-10 -top-10 w-32 h-32 rounded-full blur-xl transition-medium ${
//                 hoveredSection === 'motd' ? 'bg-accent-pink opacity-10' : 'bg-accent-purple opacity-5'
//               }`}></div>
//               <div className="flex items-center mb-2 group">
//                 <i className={`fas fa-quote-left mr-2 transition-medium transform-gpu ${
//                   hoveredSection === 'motd' ? 'text-accent-blue rotate-12 scale-110' : 'text-accent-pink'
//                 }`}></i>
//                 <span className={`transition-medium ${
//                   hoveredSection === 'motd' ? 'text-white' : 'text-gray-400 hover:text-white'
//                 }`}>Server MOTD</span>
//               </div>
//               <div className={`font-minecraft text-sm leading-relaxed pl-2 border-l-2 transition-medium ${
//                 hoveredSection === 'motd' ? 'border-accent-blue' : 'border-accent-pink hover:border-accent-purple'
//               }`}>
//                 {Array.isArray(serverData.motd) ? (
//                   serverData.motd.map((line, index) => (
//                     <p 
//                       key={index} 
//                       dangerouslySetInnerHTML={{ __html: line }} 
//                       className={`transition-medium ${
//                         hoveredSection === 'motd' ? 'text-white' : 'hover:text-white'
//                       }`} 
//                     />
//                   ))
//                 ) : (
//                   <p className={`transition-medium ${
//                     hoveredSection === 'motd' ? 'text-white' : 'hover:text-white'
//                   }`}>{serverData.motd}</p>
//                 )}
//               </div>
//             </div>
//           )}

//           {/* Floating server icon */}
//           <div className={`absolute right-0 top-0 h-full w-auto flex items-center -mr-10 transform-gpu transition-medium ${
//             hoveredSection ? 'opacity-20 scale-110' : 'opacity-10'
//           }`}>
//             <i className="fas fa-server text-9xl animate-float"></i>
//           </div>

//           {/* Connection lines animation */}
//           <div className="absolute right-0 top-0 h-full w-32 overflow-hidden pointer-events-none">
//             {[0, 1, 2].map((i) => (
//               <div 
//                 key={i}
//                 className={`absolute right-0 top-1/2 h-px w-32 bg-gradient-to-l from-accent-purple to-transparent transition-all duration-1000 ease-out ${
//                   hoveredSection ? 'opacity-30' : 'opacity-0'
//                 }`}
//                 style={{
//                   transform: `translateY(${(i - 1) * 20}px)`,
//                   transitionDelay: `${i * 100}ms`
//                 }}
//               ></div>
//             ))}
//           </div>
//         </>
//       ) : (
//         <div className="text-center text-gray-400 py-6 hover:text-white transition-medium transform-gpu hover:scale-[1.01]">
//           <i className="fas fa-server text-4xl mb-3 animate-float"></i>
//           <p>No server data available. Please select a server to test.</p>
//         </div>
//       )}
//     </div>
//   );
// };

export default ServerInfoCard;